import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MapPin, Clock, Loader2, BadgeCheck, AlertTriangle, Banknote, Lock, Star, Siren } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const claimStatus = {
  claimed: { label: "Claimed — check in on site", cls: "bg-amber-500/10 text-amber-500" },
  working: { label: "Working — checked in", cls: "bg-emerald-500/10 text-emerald-400" },
  completed: { label: "Completed", cls: "bg-sky-500/10 text-sky-400" },
  no_show: { label: "No-show — account banned", cls: "bg-red-500/10 text-red-400" },
};

const payoutLabel = {
  released: "Paid from escrow",
  pending_escrow: "Awaiting venue escrow",
};

export default function GuardWork() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [busy, setBusy] = useState("");
  const [license, setLicense] = useState("");
  const [region, setRegion] = useState("");
  const [panicArmed, setPanicArmed] = useState("");
  const [score, setScore] = useState(null);
  const [joinBusy, setJoinBusy] = useState(false);

  const isMember = !!(user && (user.founding_pass || user.membership_active));

  const joinMembership = async () => {
    setJoinBusy(true);
    try {
      const res = await fetch(`${API}/payments/membership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(d.detail));
      if (d.checkout_url) window.location.href = d.checkout_url;
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoinBusy(false);
    }
  };

  const load = async () => {
    try {
      const [mine, board, scoreRes] = await Promise.all([
        fetch(`${API}/gig/mine`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
        fetch(`${API}/shifts`).then((r) => (r.ok ? r.json() : null)),
        fetch(`${API}/gig/score/mine`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      ]);
      if (mine) setData(mine);
      if (board) setShifts(board.shifts || []);
      if (scoreRes) setScore(scoreRes);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const getPos = () =>
    new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
    );

  const act = async (key, fn) => {
    setBusy(key);
    try {
      let body = null;
      if (fn.geo) {
        try {
          const pos = await getPos();
          body = JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        } catch {
          toast.error("Location access denied — GPS proof is required on shift.");
          setBusy("");
          return;
        }
      } else if (fn.body) {
        body = JSON.stringify(fn.body);
      }
      const res = await fetch(fn.url, {
        method: "POST",
        credentials: "include",
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body,
      });
      const out = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(out.detail));
      toast.success(fn.success(out));
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const verification = data?.verification;
  const verified = verification?.status === "approved";
  const claimedIds = new Set((data?.claims || []).map((c) => c.shift_id));
  const available = shifts.filter((s) => !claimedIds.has(s.id));

  return (
    <motion.section
      data-testid="guard-work"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="mt-6"
    >
      {user && !isMember && (
        <div data-testid="membership-banner" className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div>
            <p className="font-display text-base font-bold uppercase text-slate-50">Membership unlocks claiming</p>
            <p className="mt-1 text-xs text-slate-400">£5.99/month · cancel anytime · venues pay the same to post — one membership, both sides of the board.</p>
          </div>
          <button
            data-testid="guard-join-btn"
            onClick={joinMembership}
            disabled={joinBusy}
            className="flex items-center gap-2 rounded-md bg-amber-500 px-5 py-2.5 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
          >
            {joinBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Join — £5.99/mo
          </button>
        </div>
      )}
      <div className="rounded-xl border border-white/8 bg-[#0f141c] p-8">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-amber-500" />
          <h2 className="font-display text-xl font-bold uppercase text-slate-50">Guard work</h2>
          {verified && (
            <span data-testid="verified-badge" className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono-tag text-[9px] text-emerald-400">
              <BadgeCheck className="h-3.5 w-3.5" /> License verified
            </span>
          )}
          {score && score.score !== null && (
            <span data-testid="reliability-score" className={`flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono-tag text-[9px] ${
              score.grade === "A" ? "border-amber-500/50 bg-amber-500/10 text-amber-500" :
              score.grade === "B" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" :
              "border-white/15 bg-white/5 text-slate-400"
            }`}>
              Reliability {score.grade} · {score.score}
            </span>
          )}
          {score && score.score === null && (
            <span data-testid="reliability-new" className="rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono-tag text-[9px] text-slate-500">
              Reliability: NEW
            </span>
          )}
        </div>

        {!verified && (
          <div className="mt-6">
            {verification?.status === "pending" ? (
              <div data-testid="verification-pending" className="flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-5">
                <Clock className="h-5 w-5 text-amber-500" />
                <p className="text-sm text-slate-300">License <strong>{verification.license_number}</strong> is in review. You can claim shifts once approved.</p>
              </div>
            ) : (
              <form
                data-testid="verification-form"
                onSubmit={(e) => e.preventDefault()}
                className="rounded-lg border border-white/10 bg-[#07090e] p-5"
              >
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Lock className="h-4 w-4 text-amber-500" />
                  Verify your guard license to unlock claiming. Clean submissions verify instantly — unusual ones get a human review.
                </p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <input
                    data-testid="verify-license-number"
                    required
                    value={license}
                    onChange={(e) => setLicense(e.target.value)}
                    placeholder="License / SIA number"
                    className="h-12 flex-1 rounded-md border border-white/10 bg-[#0f141c] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
                  />
                  <input
                    data-testid="verify-region"
                    required
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="State / region (e.g. TX, TX, London)"
                    className="h-12 rounded-md border border-white/10 bg-[#0f141c] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none sm:w-56"
                  />
                  <button
                    data-testid="verify-submit-btn"
                    type="button"
                    disabled={busy === "verify" || !license.trim() || !region.trim()}
                    onClick={async () => {
                      setBusy("verify");
                      try {
                        const res = await fetch(`${API}/gig/verification`, {
                          method: "POST",
                          credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ license_number: license.trim(), region: region.trim() }),
                        });
                        const out = await res.json();
                        if (!res.ok) throw new Error(formatApiErrorDetail(out.detail));
                        toast.success(out.status === "approved"
                          ? "Verified instantly — you can now claim shifts."
                          : "License submitted for review.");
                        load();
                      } catch (err) {
                        toast.error(err.message);
                      } finally {
                        setBusy("");
                      }
                    }}
                    className="flex h-12 items-center justify-center gap-2 rounded-md bg-amber-500 px-6 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
                  >
                    {busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Submit for review
                  </button>
                </div>
                {verification?.status === "rejected" && (
                  <p data-testid="verification-rejected" className="mt-3 flex items-center gap-2 text-xs text-red-400">
                    <AlertTriangle className="h-3.5 w-3.5" /> Previous submission was rejected — double-check the number and resubmit.
                  </p>
                )}
              </form>
            )}
          </div>
        )}

        {(data?.claims || []).length > 0 && (
          <div className="mt-8">
            <p className="font-mono-tag text-[10px] text-slate-500">My shifts</p>
            <ul className="mt-3 space-y-3">
              {data.claims.map((c) => (
                <li key={c.id} data-testid={`claim-${c.id}`} className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border border-white/8 bg-[#07090e] px-5 py-4">
                  <div className="min-w-[160px] flex-1">
                    <p className="font-display text-base font-bold uppercase text-slate-50">{c.shift?.venue || "Shift"}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="h-3 w-3" /> {c.shift?.date} · {c.shift?.start}–{c.shift?.end}
                    </p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${claimStatus[c.status]?.cls || "bg-white/5 text-slate-400"}`}>
                    {claimStatus[c.status]?.label || c.status}
                  </span>
                  {c.checkin_verified && (
                    <span className="font-mono-tag text-[8px] text-emerald-500">GPS verified</span>
                  )}
                  {c.status === "claimed" && (
                    <button
                      data-testid={`checkin-${c.id}`}
                      onClick={() => act(`in-${c.id}`, { url: `${API}/gig/${c.id}/checkin`, geo: true, success: (o) => o.gps_verified ? "Checked in — GPS verified." : "Checked in." })}
                      disabled={!!busy}
                      className="rounded-md bg-emerald-500 px-4 py-2 font-display text-xs font-bold uppercase text-[#07090e] transition-all hover:bg-emerald-400 disabled:opacity-50"
                    >
                      {busy === `in-${c.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Check in"}
                    </button>
                  )}
                  {c.status === "working" && (
                    <>
                      <button
                        data-testid={`checkout-${c.id}`}
                        onClick={() => act(`out-${c.id}`, { url: `${API}/gig/${c.id}/checkout`, geo: true, success: (o) => `Checked out — ${o.hours}h logged. Your GPS record is the proof of hours.` })}
                        disabled={!!busy}
                        className="rounded-md bg-amber-500 px-4 py-2 font-display text-xs font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
                      >
                        {busy === `out-${c.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Check out"}
                      </button>
                      <button
                        data-testid={`panic-${c.id}`}
                        onClick={() => {
                          if (panicArmed !== c.id) {
                            setPanicArmed(c.id);
                            toast.error("Tap again to send a panic alert with your GPS location.");
                            return;
                          }
                          setPanicArmed("");
                          act(`panic-${c.id}`, { url: `${API}/gig/${c.id}/panic`, geo: true, success: () => "Panic alert sent — ops has your location." });
                        }}
                        disabled={!!busy}
                        className={`flex items-center gap-1.5 rounded-md px-4 py-2 font-display text-xs font-bold uppercase transition-all disabled:opacity-50 ${
                          panicArmed === c.id ? "animate-pulse bg-red-600 text-white" : "bg-red-500/15 text-red-400 ring-1 ring-red-500/50 hover:bg-red-500/25"
                        }`}
                      >
                        {busy === `panic-${c.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Siren className="h-3.5 w-3.5" />}
                        {panicArmed === c.id ? "Confirm panic" : "Panic"}
                      </button>
                    </>
                  )}
                  {c.status === "completed" && (
                    <div className="flex items-center gap-3">
                      {c.payout_amount && (
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-500">
                          <Banknote className="h-4 w-4" /> ${c.payout_amount}
                        </span>
                      )}
                      {c.rated ? (
                        <span data-testid={`rated-${c.id}`} className="font-mono-tag text-[8px] text-emerald-400">Venue rated</span>
                      ) : (
                        <div data-testid={`rate-${c.id}`} className="flex items-center gap-1">
                          <span className="font-mono-tag mr-1 text-[8px] text-slate-500">Rate venue</span>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              data-testid={`rate-${c.id}-${star}`}
                              onClick={() => act(`rate-${c.id}`, {
                                url: `${API}/gig/${c.id}/rate`,
                                geo: false,
                                body: { stars: star },
                                success: () => "Venue rated — thank you.",
                              })}
                              disabled={!!busy}
                              className="text-slate-600 transition-all hover:scale-125 hover:text-amber-500 disabled:opacity-50"
                            >
                              <Star className="h-4 w-4" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8">
          <p className="font-mono-tag text-[10px] text-slate-500">Available shifts</p>
          <ul className="mt-3 space-y-3">
            {available.slice(0, 6).map((s) => (
              <li key={s.id} data-testid={`available-${s.id}`} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/5 bg-[#07090e] px-5 py-3.5">
                <div className="min-w-[160px] flex-1">
                  <p className="font-display text-base font-bold uppercase text-slate-100">{s.venue}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{s.area}, {s.city} · {s.date} · {s.start}–{s.end}</p>
                </div>
                <span className="font-display text-lg font-black text-amber-500">{s.currency === "GBP" ? "£" : "$"}{s.rate}/hr</span>
                <button
                  data-testid={`claim-${s.id}`}
                  onClick={() => act(`claim-${s.id}`, { url: `${API}/gig/claim/${s.id}`, geo: false, success: () => `Claimed ${s.venue}. Remember rule 01.` })}
                  disabled={!!busy || !verified}
                  title={verified ? "Claim this shift" : "Verify your license first"}
                  className="rounded-md bg-amber-500 px-4 py-2 font-display text-xs font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {busy === `claim-${s.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : verified ? "Claim" : "Verify to claim"}
                </button>
              </li>
            ))}
            {available.length === 0 && (
              <p className="py-4 font-mono-tag text-[9px] text-slate-600">No open shifts right now</p>
            )}
          </ul>
        </div>

        {(data?.payouts || []).length > 0 && (
          <div className="mt-8 border-t border-white/5 pt-6">
            <p className="font-mono-tag text-[10px] text-slate-500">Payout ledger</p>
            <ul className="mt-3 space-y-2">
              {data.payouts.map((p) => (
                <li key={p.session_id} data-testid={`payout-${p.session_id}`} className="flex items-center justify-between rounded-md bg-[#07090e] px-4 py-2.5 text-sm">
                  <span className="text-slate-300">{p.currency?.toUpperCase()} {p.amount}</span>
                  <span className={`font-mono-tag text-[8px] ${p.payment_status === "released" ? "text-emerald-400" : "text-amber-500"}`}>
                    {payoutLabel[p.payment_status] || p.payment_status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.section>
  );
}
